import React from 'react';

const keywordStopWords = new Set([
  'a', 'ad', 'al', 'allo', 'ai', 'agli', 'alla', 'alle', 'che', 'con', 'del', 'della', 'delle', 'degli', 'dei',
  'dal', 'dallo', 'dalla', 'dalle', 'dagli', 'dai', 'dall', 'nel', 'nella', 'nelle', 'negli', 'per', 'tra', 'fra',
  'una', 'uno', 'gli', 'le', 'la', 'il', 'lo', 'un', 'in', 'di', 'da', 'e', 'ed', 'i', 'o', 'come', 'cosa',
  'quale', 'quali', 'quando', 'quanto', 'dove', 'sono', 'sia', 'essere', 'anche', 'solo', 'sola', 'sole',
  'non', 'dell', 'all', 'nell', 'sull', 'tutti', 'tutte', 'quello', 'quella', 'quelli', 'quelle',
  'questo', 'questa', 'questi', 'queste', 'precedenti',
  'molto', 'molti', 'molte', 'poco', 'poca', 'pochi', 'poche', 'piu', 'meno',
]);

const genericKeywordWords = new Set([
  'sistema', 'sistemi', 'progettazione', 'progetto', 'oggetto', 'oggetti', 'risposta', 'risposte',
  'domanda', 'domande', 'utente', 'utenti', 'interazione', 'interazioni', 'funzione', 'funzioni',
  'funzionalita', 'elemento', 'elementi', 'processo', 'processi', 'modello', 'modelli', 'metodo',
  'metodi', 'caso', 'casi', 'livello', 'livelli', 'prodotto', 'prodotti', 'prestazione', 'prestazioni',
  'analisi', 'risultato', 'risultati',
]);

const cleanKeywordTextArtifacts = (value = '') => String(value || '')
  .replace(/[\u0f55\u0f56]/gu, '"')
  .replace(/\s*\u0f4c\s*/gu, ' - ');

const normalizeKeywordText = (value = '') => cleanKeywordTextArtifacts(value)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^\p{L}\p{N}\s]/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getKeywordWords = (value = '') => (
  cleanKeywordTextArtifacts(value).match(/[\p{L}\p{N}]+/gu)?.map((raw) => ({
    raw,
    normalized: normalizeKeywordText(raw),
  })) || []
);

const getKeywordLineValue = (line = '') => line.replace(/^[^:]+:\s*/, '').trim();

const normalizeKeywordTerm = (term = '') => {
  const cleanTerm = String(term || '').trim();
  const normalizedTerm = normalizeKeywordText(cleanTerm);

  if (/^nessuna\b/.test(normalizedTerm)) {
    return cleanTerm.match(/[\p{L}\p{N}]+/u)?.[0] || 'Nessuna';
  }

  if (/^none\b/.test(normalizedTerm)) {
    return cleanTerm.match(/[\p{L}\p{N}]+/u)?.[0] || 'None';
  }

  return cleanTerm;
};

const getLineType = (line = '', text = {}) => {
  const lowerLine = String(line || '').toLowerCase();
  const differenceLabel = String(text.keywordDifference || 'differenza').toLowerCase();
  const contextLabel = String(text.keywordContext || 'contesto').toLowerCase();

  if (lowerLine.startsWith(`${differenceLabel}:`) || /^(differenza|difference)\s*:/i.test(line)) {
    return 'difference';
  }

  if (lowerLine.startsWith(`${contextLabel}:`) || /^(contesto|context)\s*:/i.test(line)) {
    return 'context';
  }

  return 'user';
};

const getKeywordClassName = (lineType) => {
  if (lineType === 'difference') {
    return 'quiz-keyword-highlight-difference';
  }

  if (lineType === 'context') {
    return 'quiz-keyword-highlight-context';
  }

  return 'quiz-keyword-highlight-user';
};

const getKeywordTermsFromHint = (keywordHint = '', text = {}) => {
  const cleanHint = String(keywordHint || '').trim();

  if (!cleanHint) {
    return [];
  }

  const lines = cleanHint
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const preferredTypes = ['difference', 'context', 'user'];
  const selectedType = preferredTypes.find((lineType) => lines.some((line) => getLineType(line, text) === lineType)) || 'user';
  const selectedLines = lines.filter((line) => getLineType(line, text) === selectedType);
  const className = getKeywordClassName(selectedType);
  const terms = selectedLines
    .flatMap((line) => getKeywordLineValue(line).split(/[,;\n]/))
    .map((term) => normalizeKeywordTerm(term))
    .filter((term) => {
      const words = getKeywordWords(term);
      return term.length > 2 && term.length <= 90 && words.length > 0 && words.length <= 9;
    })
    .map((term) => ({ term, className }));

  return [...new Map(terms.map((termInfo) => [
    normalizeKeywordText(termInfo.term),
    termInfo,
  ])).values()];
};

export const getSavedKeywordHint = (question = {}) => (
  String(question.keywordHint ?? question.keyword_hint ?? '').trim()
);

export const isQuestionKeywordHintDisabled = (question = {}) => {
  const disabledValue = question.keywordHintDisabled ?? question.keyword_hint_disabled;

  return disabledValue === true
    || disabledValue === 1
    || disabledValue === '1'
    || disabledValue === 'true';
};

const cleanKeywordTerm = (value = '') => cleanKeywordTextArtifacts(value)
  .replace(/^[,;:.\s]+|[,;:.\s]+$/g, '')
  .trim();

const hasKeywordPhrase = (textValue = '', phrase = '') => {
  const textWords = getKeywordWords(textValue).map((word) => word.normalized);
  const phraseWords = getKeywordWords(phrase).map((word) => word.normalized);

  if (!textWords.length || !phraseWords.length || phraseWords.length > textWords.length) {
    return false;
  }

  for (let index = 0; index <= textWords.length - phraseWords.length; index += 1) {
    if (phraseWords.every((word, wordIndex) => textWords[index + wordIndex] === word)) {
      return true;
    }
  }

  return false;
};

const isAllowedShortKeyword = (word, correctText = '') => (
  word
  && /^[a-zA-Z0-9]$/.test(word.raw)
  && (
    /\d/.test(word.raw)
    || new RegExp(`\\blettera\\s+${escapeKeywordRegex(word.raw)}\\b`, 'iu').test(correctText)
    || new RegExp(`\\bletter\\s+${escapeKeywordRegex(word.raw)}\\b`, 'iu').test(correctText)
  )
);

const isWeakAutomaticKeyword = (word, correctText = '') => (
  !word
  || (
    word.normalized.length < 3
    && !isAllowedShortKeyword(word, correctText)
  )
  || keywordStopWords.has(word.normalized)
  || genericKeywordWords.has(word.normalized)
);

const isNegationKeyword = (word) => word && ['non', 'senza'].includes(word.normalized);

const isPhraseEdgeStopWord = (word) => (
  !word
  || keywordStopWords.has(word.normalized)
  || (
    word.normalized.length < 3
    && !isAllowedShortKeyword(word)
  )
);

const getPhraseCombinationCandidates = (correctText, wrongTexts, correctWords) => {
  const phraseCandidatesByText = new Map();

  for (let size = 2; size <= Math.min(5, correctWords.length); size += 1) {
    for (let index = 0; index <= correctWords.length - size; index += 1) {
      const phraseWords = correctWords.slice(index, index + size);
      const strongWords = phraseWords.filter((word) => !isWeakAutomaticKeyword(word, correctText));
      const hasNegation = phraseWords.some(isNegationKeyword);

      if (
        (!strongWords.length && !hasNegation)
        || isPhraseEdgeStopWord(phraseWords[phraseWords.length - 1])
      ) {
        continue;
      }

      const rawPhrase = cleanKeywordTerm(phraseWords.map((word) => word.raw).join(' '));
      const normalizedPhrase = normalizeKeywordText(rawPhrase);

      if (!rawPhrase || rawPhrase.length > 90 || !normalizedPhrase || phraseCandidatesByText.has(normalizedPhrase)) {
        continue;
      }

      const missingWrongIndexes = wrongTexts
        .map((wrongText, wrongIndex) => (hasKeywordPhrase(wrongText, rawPhrase) ? null : wrongIndex))
        .filter((wrongIndex) => wrongIndex !== null);

      if (!missingWrongIndexes.length) {
        continue;
      }

      const weakEdgePenalty = (
        (isPhraseEdgeStopWord(phraseWords[0]) ? 3 : 0)
        + (isPhraseEdgeStopWord(phraseWords[phraseWords.length - 1]) ? 12 : 0)
      );

      phraseCandidatesByText.set(normalizedPhrase, {
        raw: rawPhrase,
        normalized: normalizedPhrase,
        index,
        length: size,
        missingWrongIndexes,
        isPhrase: true,
        score: (
          (missingWrongIndexes.length * 30)
          + (strongWords.length * 8)
          + (hasNegation ? 8 : 0)
          - (size * 6)
          - weakEdgePenalty
          - (index * 0.01)
        ),
      });
    }
  }

  return [...phraseCandidatesByText.values()];
};

const getCoveredCandidateCombination = (candidates, wrongAnswersCount) => {
  const coveredWrongIndexes = new Set();
  const selectedCandidates = [];
  const eligibleCandidates = candidates.filter((candidate) => !candidate.isRiskyCombinationTerm);

  while (coveredWrongIndexes.size < wrongAnswersCount && selectedCandidates.length < 4) {
    const nextCandidate = eligibleCandidates
      .filter((candidate) => !selectedCandidates.some((selected) => selected.normalized === candidate.normalized))
      .map((candidate) => ({
        ...candidate,
        newlyCoveredIndexes: candidate.missingWrongIndexes.filter((wrongIndex) => !coveredWrongIndexes.has(wrongIndex)),
      }))
      .filter((candidate) => candidate.newlyCoveredIndexes.length)
      .sort((firstCandidate, secondCandidate) => (
        (secondCandidate.newlyCoveredIndexes.length - firstCandidate.newlyCoveredIndexes.length)
        || ((firstCandidate.isPhrase ? 1 : 0) - (secondCandidate.isPhrase ? 1 : 0))
        || ((firstCandidate.length || 1) - (secondCandidate.length || 1))
        || (secondCandidate.score - firstCandidate.score)
        || (firstCandidate.index - secondCandidate.index)
      ))[0];

    if (!nextCandidate) {
      break;
    }

    selectedCandidates.push(nextCandidate);
    nextCandidate.newlyCoveredIndexes.forEach((wrongIndex) => coveredWrongIndexes.add(wrongIndex));
  }

  return coveredWrongIndexes.size === wrongAnswersCount ? selectedCandidates : [];
};

const getDistinctivePhraseTerms = (correctText, wrongTexts, correctWords) => {
  const phraseCandidates = [];

  for (let size = Math.min(4, correctWords.length); size >= 2; size -= 1) {
    for (let index = 0; index <= correctWords.length - size; index += 1) {
      const phraseWords = correctWords.slice(index, index + size);

      if (
        isWeakAutomaticKeyword(phraseWords[0], correctText)
        || isWeakAutomaticKeyword(phraseWords[phraseWords.length - 1], correctText)
      ) {
        continue;
      }

      const rawPhrase = cleanKeywordTerm(phraseWords.map((word) => word.raw).join(' '));

      if (rawPhrase && !wrongTexts.some((wrongText) => hasKeywordPhrase(wrongText, rawPhrase))) {
        phraseCandidates.push({
          term: rawPhrase,
          index,
          length: size,
        });
      }
    }
  }

  return phraseCandidates
    .sort((firstPhrase, secondPhrase) => (
      (secondPhrase.length - firstPhrase.length)
      || (firstPhrase.index - secondPhrase.index)
    ))
    .slice(0, 2)
    .sort((firstPhrase, secondPhrase) => firstPhrase.index - secondPhrase.index)
    .map((phrase) => ({
      term: phrase.term,
      className: 'quiz-keyword-highlight-difference',
    }));
};

const getAutomaticKeywordTerms = (question = {}) => {
  if (
    String(question.questionType || '').toLowerCase() !== 'closed'
    || isQuestionKeywordHintDisabled(question)
  ) {
    return [];
  }

  const answers = Array.isArray(question.answers) ? question.answers : [];
  const correctAnswers = answers.filter((answer) => answer.isCorrect);
  const wrongAnswers = answers.filter((answer) => !answer.isCorrect);

  if (!correctAnswers.length || !wrongAnswers.length) {
    return [];
  }

  const correctText = correctAnswers.map((answer) => answer.text).join(' ');
  const wrongTexts = wrongAnswers.map((answer) => answer.text);
  const wrongWordSets = wrongTexts.map((wrongText) => new Set(
    getKeywordWords(wrongText).map((word) => word.normalized)
  ));
  const correctWords = getKeywordWords(correctText);
  const specialWord = correctWords.find((word) => ['nessuna', 'nessuno', 'none'].includes(word.normalized));

  if (specialWord) {
    return [{
      term: specialWord.raw,
      className: 'quiz-keyword-highlight-difference',
    }];
  }

  const candidatesByWord = new Map();

  correctWords.forEach((word, index) => {
    const isNegationCandidate = isNegationKeyword(word);

    if (isWeakAutomaticKeyword(word, correctText) && !isNegationCandidate) {
      return;
    }

    const missingWrongIndexes = wrongWordSets
      .map((wrongWordSet, wrongIndex) => (wrongWordSet.has(word.normalized) ? null : wrongIndex))
      .filter((wrongIndex) => wrongIndex !== null);

    if (!missingWrongIndexes.length || candidatesByWord.has(word.normalized)) {
      return;
    }

    candidatesByWord.set(word.normalized, {
      ...word,
      index,
      missingWrongIndexes,
      isCombinationOnly: isNegationCandidate,
      isPhrase: false,
      isRiskyCombinationTerm: (
        word.normalized.length < 2
        && missingWrongIndexes.length < wrongWordSets.length
      ),
      score: (missingWrongIndexes.length * 30) + Math.min(word.normalized.length, 18),
    });
  });

  const wordCandidates = [...candidatesByWord.values()];
  const phraseCandidates = getPhraseCombinationCandidates(correctText, wrongTexts, correctWords);
  const candidates = [...wordCandidates, ...phraseCandidates]
    .sort((firstCandidate, secondCandidate) => (
      (secondCandidate.score - firstCandidate.score)
      || (firstCandidate.index - secondCandidate.index)
    ));
  const uniqueCandidates = candidates.filter((candidate) => (
    candidate.missingWrongIndexes.length === wrongWordSets.length
    && !candidate.isCombinationOnly
  ));
  const uniqueWordCandidates = uniqueCandidates.filter((candidate) => !candidate.isPhrase);
  const uniquePhraseCandidates = uniqueCandidates.filter((candidate) => candidate.isPhrase);
  const selectedCandidates = uniqueWordCandidates.length
    ? uniqueWordCandidates.slice(0, 4)
    : uniquePhraseCandidates.length
        ? uniquePhraseCandidates
            .sort((firstCandidate, secondCandidate) => (
              ((firstCandidate.length || 1) - (secondCandidate.length || 1))
              || (secondCandidate.score - firstCandidate.score)
              || (firstCandidate.index - secondCandidate.index)
            ))
            .slice(0, 1)
        : getCoveredCandidateCombination(candidates, wrongWordSets.length);

  if (!selectedCandidates.length) {
    return getDistinctivePhraseTerms(correctText, wrongTexts, correctWords);
  }

  return selectedCandidates
    .sort((firstCandidate, secondCandidate) => firstCandidate.index - secondCandidate.index)
    .map((candidate) => ({
      term: candidate.raw,
      className: 'quiz-keyword-highlight-difference',
    }));
};

export const getQuestionKeywordTerms = (question = {}, text = {}) => {
  const savedKeywordTerms = getKeywordTermsFromHint(getSavedKeywordHint(question), text);

  return savedKeywordTerms.length ? savedKeywordTerms : getAutomaticKeywordTerms(question);
};

export const getAutomaticKeywordHint = (question = {}, text = {}) => {
  const automaticTerms = getAutomaticKeywordTerms(question).map((termInfo) => termInfo.term);

  return automaticTerms.length
    ? `${text.keywordDifference || 'Differenza'}: ${automaticTerms.join(', ')}`
    : '';
};

const escapeKeywordRegex = (value = '') => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getKeywordRegexSource = (term = '') => {
  const keywordWords = getKeywordWords(term).map((word) => word.raw).filter(Boolean);
  const termSource = keywordWords.length > 1
    ? keywordWords.map(escapeKeywordRegex).join('[^\\p{L}\\p{N}]+')
    : escapeKeywordRegex(term.trim());

  return termSource ? `(?<![\\p{L}\\p{N}])${termSource}(?![\\p{L}\\p{N}])` : '';
};

export const renderHighlightedText = (value = '', keywordTerms = []) => {
  const safeText = String(value || '');
  const matchingTerms = keywordTerms
    .map((termInfo) => ({ ...termInfo, source: getKeywordRegexSource(termInfo.term) }))
    .filter(({ source }) => source && new RegExp(source, 'iu').test(safeText))
    .sort((firstTerm, secondTerm) => secondTerm.term.length - firstTerm.term.length);

  if (!matchingTerms.length) {
    return safeText;
  }

  const keywordRegex = new RegExp(`(${matchingTerms.map(({ source }) => source).join('|')})`, 'giu');
  const highlightedParts = [];
  let lastIndex = 0;

  Array.from(safeText.matchAll(keywordRegex)).forEach((match, index) => {
    const part = match[0];
    const matchIndex = match.index || 0;
    const matchedKeyword = matchingTerms.find(({ source }) => (
      new RegExp(`^${source}$`, 'iu').test(part)
    ));

    if (
      matchedKeyword
      && normalizeKeywordText(matchedKeyword.term) !== 'non'
      && /\bnon\s*$/iu.test(safeText.slice(Math.max(0, matchIndex - 8), matchIndex))
    ) {
      return;
    }

    if (matchIndex > lastIndex) {
      highlightedParts.push(safeText.slice(lastIndex, matchIndex));
    }

    if (!matchedKeyword) {
      highlightedParts.push(part);
      lastIndex = matchIndex + part.length;
      return;
    }

    highlightedParts.push(
      <mark key={`${part}-${index}`} className={`quiz-keyword-highlight ${matchedKeyword.className}`}>
        {part}
      </mark>
    );
    lastIndex = matchIndex + part.length;
  });

  if (lastIndex < safeText.length) {
    highlightedParts.push(safeText.slice(lastIndex));
  }

  return highlightedParts.length ? highlightedParts : safeText;
};
